import React, { useState } from "react";
import { Box, Grid, Typography, Dialog, DialogContent, IconButton, Divider, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { SUPPORT_CATEGORIES, SupportCategory } from "../../pages/support/supportCategories";
import { useAuth } from "../../hooks/useAuth";

const TEAL = "#00B3B5";

interface SupportCategoriesGridProps {
  // Called when the user says a resolution guide didn't help. Omit to just close the modal.
  onNotHelpful?: () => void;
  // "preLogin" is the redesigned Help Center treatment: smaller headings, bordered cards
  // instead of raised ones. The logged-in / QRC-verified Contact Us hub keeps "default".
  variant?: "default" | "preLogin";
  // "rail" is the teal "Before you raise a request" side panel used alongside the verify /
  // raise-a-request card; it opens the same resolution guides as the grid.
  layout?: "grid" | "rail";
  // Rail only. On: the panel is taken out of flow to match the height of the card beside it and
  // the tiles scroll inside — used in the pre-login shell, where the two form a single block.
  // Off (default): the panel sizes to its tiles, so it never trails a long page with empty teal.
  railFillHeight?: boolean;
}

// "Common Support Categories" cards + resolution guide modal — shown on the pre-login Help
// Center, as a side rail next to the verify / raise-request card, and on the logged-in /
// QRC-verified Contact Us hub (see ContactUsHub).
const SupportCategoriesGrid: React.FC<SupportCategoriesGridProps> = ({
  onNotHelpful,
  variant = "default",
  layout = "grid",
  railFillHeight = false,
}) => {
  const [activeGuide, setActiveGuide] = useState<SupportCategory | null>(null);
  const isPreLogin = variant === "preLogin";
  // Real login state, not the `variant`/`layout` props: the rail is rendered both pre-login
  // (Help Center, QRC Contact Us) and post-login, so only the auth session distinguishes them.
  const { isAuthenticated } = useAuth();

  if (layout === "rail") {
    return (
      <>
        <Box
          sx={{
            // When filling: taken out of flow on desktop (its wrapper is position:relative) so the
            // tile list can never drive the row's height — the card beside it does — and the panel
            // fills exactly that height. Stretching alone did not work, because the rail's content
            // was the taller of the two, so the row grew to fit it.
            ...(railFillHeight ? { position: { md: "absolute" }, inset: { md: 0 } } : null),
            height: { md: "auto" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: "12px",
            px: 2,
            pt: 3,
            pb: 2.5,
            background: "linear-gradient(180deg, #14A3A6 0%, #0A6E72 100%)",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>
            Before you raise a request
          </Typography>
          <Typography sx={{ fontSize: "12.5px", color: "rgba(255,255,255,0.85)", mt: 0.75, lineHeight: 1.6 }}>
            Most queries are answered instantly here. No waiting for response, check if your question is
            already covered.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              mt: 2,
              // Only when filling: take the leftover height and scroll. On mobile the panel is
              // stacked, so it grows with its content rather than trapping a scroll area in the
              // page flow — and the same applies whenever the panel is content-sized.
              ...(railFillHeight
                ? { flex: { md: 1 }, overflowY: { xs: "visible", md: "auto" }, pr: { md: 0.5 } }
                : null),
              minHeight: 0,
              "&::-webkit-scrollbar": { width: 6 },
              // Chrome renders stepper arrows on a styled scrollbar unless they're suppressed.
              "&::-webkit-scrollbar-button": { display: "none", height: 0, width: 0 },
              "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(255,255,255,0.35)",
                borderRadius: 3,
              },
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(255,255,255,0.35) transparent",
            }}
          >
            {SUPPORT_CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <Box
                  key={category.title}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveGuide(category)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setActiveGuide(category);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    p: 2,
                    borderRadius: "8px",
                    bgcolor: "rgba(255,255,255,0.10)",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "6px",
                      bgcolor: "rgba(255,255,255,0.16)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ color: "#fff", fontSize: 17 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#fff" }}>
                      {category.title}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.75)" }}>
                      {category.subtitle}
                    </Typography>
                  </Box>
                  <ArrowForwardIcon sx={{ color: "#fff", fontSize: 18, ml: "auto", flexShrink: 0 }} />
                </Box>
              );
            })}
          </Box>
        </Box>

        {guideDialog(activeGuide, setActiveGuide, isAuthenticated, onNotHelpful)}
      </>
    );
  }

  return (
    <>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: isPreLogin ? { xs: "16px", sm: "18px" } : { xs: "22px", sm: "28px" },
          color: "#1a1a1a",
        }}
      >
        Quick Support Hub
      </Typography>
      <Typography
        sx={{
          color: "#6b7280",
          fontSize: isPreLogin ? "13px" : "14px",
          mt: 0.5,
          mb: isPreLogin ? 2.5 : 3,
        }}
      >
        Select any category below to immediately view the resolution guide
      </Typography>

      {/* 10px gutters in the pre-login design, vs the hub's roomier 20px */}
      <Grid container spacing={isPreLogin ? 1.25 : 2.5} sx={{ mb: isPreLogin ? 0 : 4 }}>
        {SUPPORT_CATEGORIES.map((category) => {
          const Icon = category.icon;
          return (
            // Pre-login keeps two columns on mobile (per design); the hub stays single-column.
            <Grid item xs={isPreLogin ? 6 : 12} sm={6} md={4} key={category.title}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setActiveGuide(category)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setActiveGuide(category);
                }}
                sx={{
                  bgcolor: "#fff",
                  borderRadius: isPreLogin ? "10px" : "12px",
                  border: isPreLogin ? "1px solid #EAECEE" : "none",
                  boxShadow: isPreLogin ? "none" : "0px 4px 18px 0px rgba(75, 70, 92, 0.08)",
                  p: isPreLogin ? { xs: 1.5, sm: 2 } : 2.5,
                  height: "100%",
                  cursor: "pointer",
                  transition: "box-shadow 0.2s, transform 0.2s, border-color 0.2s",
                  "&:hover": isPreLogin
                    ? { borderColor: TEAL, boxShadow: "0px 4px 14px 0px rgba(75, 70, 92, 0.08)" }
                    : {
                        boxShadow: "0px 8px 24px 0px rgba(75, 70, 92, 0.14)",
                        transform: "translateY(-2px)",
                      },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <Box
                    sx={{
                      width: isPreLogin ? 30 : 44,
                      height: isPreLogin ? 30 : 44,
                      borderRadius: isPreLogin ? "8px" : "10px",
                      bgcolor: "rgba(0,179,181,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: TEAL, fontSize: isPreLogin ? 17 : 24 }} />
                  </Box>
                  {isPreLogin ? (
                    <ArrowForwardIcon sx={{ color: TEAL, fontSize: 16 }} />
                  ) : (
                    <ArrowOutwardIcon sx={{ color: TEAL, fontSize: 20 }} />
                  )}
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: isPreLogin ? { xs: "13px", sm: "14px" } : "15px",
                    color: "#1a1a1a",
                    mt: isPreLogin ? 1.75 : 2,
                  }}
                >
                  {category.title}
                </Typography>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: isPreLogin ? { xs: "11px", sm: "12px" } : "13px",
                    mt: 0.5,
                  }}
                >
                  {category.subtitle}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {guideDialog(activeGuide, setActiveGuide, isAuthenticated, onNotHelpful)}
    </>
  );
};

// Inline markers inside a guide line:
//   "**…**"        bold — the source copy emphasises lead-ins like "Important Note:"
//   "[text](url)"  a link, e.g. the Schedule of Charges page
// Split on both at once so a line can carry either, in any order.
const INLINE_MARKERS = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
const LINK_MARKER = /^\[([^\]]+)\]\(([^)]+)\)$/;

const renderInline = (text: string): React.ReactNode[] =>
  text.split(INLINE_MARKERS).map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <Box component="span" key={index} sx={{ fontWeight: 700 }}>
          {part.slice(2, -2)}
        </Box>
      );
    }

    const link = LINK_MARKER.exec(part);
    if (link) {
      const href = link[2];
      // A relative href ("/contact-us/raise-request") is a page of this portal, so it opens in
      // the same tab. Anything absolute goes off-site (avanse.com) and opens in a new tab with
      // noopener/noreferrer, so the target never gets a handle on our window.
      const isExternal = /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//");
      return (
        <Box
          component="a"
          key={index}
          href={href}
          {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          sx={{ color: TEAL, fontWeight: 600, textDecoration: "underline" }}
        >
          {link[1]}
        </Box>
      );
    }

    return part;
  });

// One line of a resolution guide. The copy in supportCategories carries its own structure
// markers — "## " section heading, "### " sub-heading, "• " bullet, "o " nested bullet, and
// anything else a paragraph — so the guides can hold real article copy rather than the flat
// numbered checklist this modal originally showed.
const GuideLine: React.FC<{ line: string; isFirst: boolean }> = ({ line, isFirst }) => {
  if (line.startsWith("## ") || line.startsWith("### ")) {
    const isSub = line.startsWith("### ");
    return (
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: isSub ? "13.5px" : "15px",
          color: isSub ? "#374151" : "#1a1a1a",
          // The first heading sits directly under "Resolution Instructions", which brings its own
          // spacing; later ones need the gap to separate them from the block above.
          mt: isFirst ? 0 : isSub ? 2 : 2.5,
          mb: 1,
        }}
      >
        {line.slice(isSub ? 4 : 3)}
      </Typography>
    );
  }

  const isNested = line.startsWith("o ");
  if (isNested || line.startsWith("• ")) {
    return (
      <Box sx={{ display: "flex", gap: 1, mb: 1, pl: isNested ? 3 : 0.5 }}>
        <Typography sx={{ color: TEAL, fontSize: "14px", lineHeight: 1.6 }}>
          {isNested ? "◦" : "•"}
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "#374151", lineHeight: 1.6 }}>
          {renderInline(line.slice(2))}
        </Typography>
      </Box>
    );
  }

  return (
    <Typography sx={{ fontSize: "14px", color: "#374151", lineHeight: 1.6, mb: 1.5 }}>
      {renderInline(line)}
    </Typography>
  );
};

// Resolution guide modal — shared by the grid and the rail.
const guideDialog = (
  activeGuide: SupportCategory | null,
  setActiveGuide: (category: SupportCategory | null) => void,
  // Post-login the answer is just feedback, so the button says what it means ("No, It Didn't").
  // Pre-login it doubles as the way into raising a request, so it keeps that wording.
  isPostLogin: boolean,
  onNotHelpful?: () => void
) => (
  <Dialog
        open={!!activeGuide}
        onClose={() => setActiveGuide(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "22px", color: "#1a1a1a" }}>
                {activeGuide?.title}
              </Typography>
              {/* The category's own subtitle, as in the source copy — the modal used to show a
                  standing "Support Resolution Guide" line that exists nowhere in the content. */}
              <Typography sx={{ color: "#6b7280", fontSize: "14px", mt: 0.5, lineHeight: 1.6 }}>
                {activeGuide?.subtitle}
              </Typography>
            </Box>
            <IconButton onClick={() => setActiveGuide(null)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ mt: 3 }}>
            {activeGuide?.guide.map((line, index) => (
              <GuideLine key={index} line={line} isFirst={index === 0} />
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#1a1a1a", mb: 1.5 }}>
            Was this resolution helpful to you?
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => setActiveGuide(null)}
              sx={{
                borderColor: TEAL,
                color: TEAL,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
                "&:hover": { borderColor: "#009799", bgcolor: "rgba(0,179,181,0.04)" },
              }}
            >
              👍 Yes, Helped me
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                // Close first, then hand over: the guide didn't answer the question, so this
                // answer is the way into raising a request. The click used to only dismiss the
                // dialog — onNotHelpful was never invoked from anywhere.
                setActiveGuide(null);
               
                // Which screens act on this is decided by whoever renders the grid: a screen
                // that should send the customer on passes onNotHelpful, one that should just
                // dismiss passes nothing.
                
              }}
              sx={{
                borderColor: "#e0e0e0",
                color: "#374151",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
                "&:hover": { borderColor: "#bdbdbd", bgcolor: "#fafafa" },
              }}
            >
              {isPostLogin ? "👎 No, It Didn't" : "👎 No, Raise a request"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
);

export default SupportCategoriesGrid;
